import { Content, ContentRelationshipField } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { CaseStudyDocument } from '../../../prismicio-types';
import { PrismicNextImage } from '@prismicio/next';
import { PlainRightArrow } from '@/assets/PlainRightArrow';
import Link from 'next/link';

/**
 * Props for `HeroClients`.
 */
export type HeroClientsProps = SliceComponentProps<Content.HeroClientsSlice>;

/**
 * Component for "HeroClients" Slices.
 */
const HeroClients = ({ slice }: HeroClientsProps): JSX.Element => {
  const {
    primary: { title, description, clients },
  } = slice;
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="max-w-screen-2xl mx-auto px-5 md:px-16 pb-28"
    >
      <div className="flex flex-col gap-y-20">
        <div className="text-white max-w-screen-md flex flex-col gap-y-6 mt-4">
          <PrismicRichText field={title} />
          <PrismicRichText
            field={description}
            components={{
              paragraph: ({ children }) => (
                <p className="text-medium-normal">{children}</p>
              ),
            }}
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {clients.map(({ client }, index) => (
            <ClientCard
              key={`client-${index}`}
              client={
                client as ContentRelationshipField<'case_study'> &
                  CaseStudyDocument
              }
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const ClientCard = ({
  client,
}: {
  client: ContentRelationshipField<'case_study'> & CaseStudyDocument;
}) => {
  return (
    <Link href={`/clients/${client.uid}`}>
      <div className="relative hover:opacity-80">
        <PrismicNextImage
          field={client.data.image}
          className="rounded-[20px] border border-green-100"
        />
        <div className="absolute bottom-2 right-2 lg:bottom-5 lg:right-5 flex items-center gap-x-2 lg:gap-x-4">
          <span className="text-medium-normal font-bold text-white">
            See Case Study
          </span>
          <div className="bg-header-gradient rounded-[50px] w-12 h-12 backdrop-blur-sm flex justify-center items-center">
            <PlainRightArrow fill="white" arrowFill="white" />
          </div>
        </div>
      </div>
    </Link>
  );
};

export default HeroClients;
